import { UserGroupIcon } from "@heroicons/react/20/solid";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { AccessList, ListedGroup } from "../../../api/access/accessModel";
import AppButton from "../../../ui/buttons/AppButton";
import EmptyState from "../../../ui/feedback/EmptyState";
import type { MenuItem } from "../../../ui/overlays/MenuButton";
import { classNames } from "../../../ui/styles/classNames";
import type { AccessChanges } from "../changes/useAccessChanges";
import InviteDialog from "../invitations/InviteDialog";
import GroupMenu from "../list/GroupMenu";
import { groupMenuSections } from "../list/groupMenuSections";
import GroupMembers from "../members/GroupMembers";
import type { GroupNamer } from "../model/groupNames";
import GroupPermissions from "./GroupPermissions";
import IncludedPermissions from "./IncludedPermissions";
import RenameGroupDialog from "./RenameGroupDialog";

interface GroupScreenProps {
  list: AccessList;
  groupId: string;
  /** The project's name on its own screen, which an invitation names; `null` in Administration. */
  projectName: string | null;
  namer: GroupNamer;
  changes: AccessChanges;
  /** The list's page and its name, for the way back: "Access", "Groups". */
  back: { readonly path: string; readonly label: string };
  /** Removing it, or for an included group taking it out of the project; `null` where neither is offered. */
  removal: { readonly label: string; readonly run: () => void } | null;
}

/**
 * A group on one screen (redesign R3-3 `group`, `included`, `admin-group`): what its members
 * get, and who they are. An installation group in a project shows the installation's part
 * locked and what the project switches on or off; its members stay the installation's. Whoever
 * manages the group invites people into it from here (R3-4).
 */
export default function GroupScreen({ list, groupId, projectName, namer, changes, back, removal }: GroupScreenProps) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [inviting, setInviting] = useState(false);
  const index = list.groups.findIndex((group) => group.groupId === groupId);
  const group = list.groups[index];
  if (group === undefined) return <EmptyState action={<Link to={back.path} className="underline">{back.label}</Link>}>{t("access.groupGone")}</EmptyState>;
  // The project an installation group is included in, on that project's screen.
  const includedIn = group.projectId === null ? list.projectId : null;
  const included = includedIn !== null;
  const name = namer.name(group.groupId);
  const menu: MenuItem[] = [];
  if (group.manageable && group.system === null && !included) {
    menu.push({ id: "rename", label: t("access.rename"), action: { kind: "select", onSelect: () => { setRenaming(true); } } });
  }
  if (removal !== null && !included) menu.push({ id: "remove", label: removal.label, action: { kind: "select", onSelect: removal.run } });
  // The same menu as the group's row in the list: where it may move, then the rest.
  const sections = groupMenuSections(t, list, group.groupId, namer, changes, menu);
  // An invitation carries a group its sender manages, never Everyone, nor the Administrators an
  // administrator's invitation chooses (GS-7); an included group's members are the installation's.
  const mayInvite = group.manageable && !included && group.system !== "everyone" && group.system !== "administrators";

  return (
    <div className="space-y-4">
      <nav aria-label={t("access.breadcrumb")} className="app-text-muted text-xs">
        <Link to={back.path} className="hover:underline">{back.label}</Link>
        <span aria-hidden="true">{" › "}</span>
        <span className="font-semibold">{name}</span>
      </nav>
      <header className="flex flex-wrap items-center gap-3">
        <span className={classNames("flex h-8 w-8 items-center justify-center rounded", group.projectId === null ? "bg-[var(--app-warning-soft)]" : "bg-[var(--app-surface-muted)]")} aria-hidden="true">
          <UserGroupIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="app-title text-xl font-bold">{name}</h3>
          <p className="app-text-muted text-xs"><Subtitle group={group} list={list} position={index + 1} included={included} /></p>
        </div>
        {included && removal !== null ? <AppButton variant="secondary" size="sm" disabled={changes.busy} onClick={removal.run}>{removal.label}</AppButton> : null}
        {mayInvite ? <AppButton variant="primary" size="sm" onClick={() => { setInviting(true); }}>{t("access.invite.button")}</AppButton> : null}
        {sections.length === 0 ? null : <GroupMenu name={name} sections={sections} disabled={changes.busy} />}
      </header>
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        {includedIn !== null
          ? <IncludedPermissions projectId={includedIn} group={group} list={list} namer={namer} changes={changes} />
          : <GroupPermissions group={group} list={list} namer={namer} changes={changes} />}
        <GroupMembers group={group} list={list} namer={namer} changes={changes} />
      </div>
      {renaming ? (
        <RenameGroupDialog group={group} name={name} changes={changes} onClose={() => { setRenaming(false); }}
          parents={list.projectId === null ? possibleParents(list, group).map((parent) => ({ groupId: parent.groupId, name: namer.name(parent.groupId) })) : undefined} />
      ) : null}
      {inviting ? (
        <InviteDialog title={t("access.invite.title", { group: name })}
          hint={projectName === null ? t("access.invite.joins", { groups: name }) : t("access.invite.joinsIn", { group: name, project: projectName })}
          carries={{ groups: [group.groupId], accountRole: "member" }} onClose={() => { setInviting(false); }} />
      ) : null}
    </div>
  );
}

/** The installation groups a group may sit inside: people's own groups, neither itself nor one inside it. */
function possibleParents(list: AccessList, group: ListedGroup): readonly ListedGroup[] {
  const inside = new Set([group.groupId]);
  for (const listed of list.groups) {
    if (listed.parentId !== null && inside.has(listed.parentId)) inside.add(listed.groupId);
  }
  return list.groups.filter((listed) => listed.system === null && !inside.has(listed.groupId));
}

function Subtitle({ group, list, position, included }: { group: ListedGroup; list: AccessList; position: number; included: boolean }): ReactNode {
  const { t } = useTranslation();
  const kind = group.projectId !== null ? t("access.kind.project") : t("access.kind.installation");
  const size = group.system === "everyone" ? t("access.everyoneWithAccount") : t("access.peopleCount", { count: group.peopleIds.length });
  const place = included ? t("access.includedHere") : t("access.position", { ordinal: true, count: position, total: list.groups.length });
  return `${kind} · ${size} · ${place}`;
}
